import { InvestorProfileType } from '@/api/backend/types';
import { ArrowBackFilled, OpenInNewFilled } from '@/assets/icons';
import { Container } from '@/components/container';
import { PermissionGuard } from '@/components/permission-guard';
import { SubNavContainer } from '@/components/sub-nav-container';
import { PROFILE_PERMISSION } from '@/lib/constants';
import { DASHBOARD_SECTIONS, DashboardSectionId, scrollToDashboardSection } from '@/lib/dashboard-sections';
import { cn, getAvatarInitials, getLocalizedName } from '@/lib/utils';
import { useAuthStore } from '@/store/auth';
import {
  Alert,
  AlertActions,
  AlertDescription,
  AlertTitle,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  TabsList,
  TabsRoot,
  TabsTrigger,
} from '@opaz/ui-components';
import { createFileRoute, Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_auth/dashboard/_layout')({
  component: RouteComponent,
  context: () => {
    return {
      getBreadcrumbData: () => ({
        id: null,
        translationKey: null,
      }),
    };
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const { activeProfile } = useAuthStore();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const tabValue = location.pathname.split('/').pop() as string;

  const goToSection = (
    to:
      | '/dashboard/workflows'
      | '/dashboard/contracts'
      | '/dashboard/rental-contracts'
      | '/dashboard/permits'
      | '/dashboard/service-providers'
      | '/dashboard/properties',
    hash: DashboardSectionId,
  ) => {
    void navigate({ to, hash }).then(() => {
      scrollToDashboardSection(hash);
    });
  };

  const renderVerificationBanner = () => {
    if (activeProfile?.type === 'COMPANY') {
      return null;
    }

    const investorProfile = activeProfile as InvestorProfileType;
    const isResident = investorProfile?.personalDetails?.resident;
    const isVerified = investorProfile?.personalDetails?.status === 'VERIFIED';

    // TODO: Remove the isPkiLoginV2 check when the new pki login is released
    const isPkiLoginV2 = localStorage.getItem('pkiLogin') === 'v2';

    if (isResident && !isVerified && !isPkiLoginV2) {
      return (
        <Alert variant="warning" className="mb-2">
          <AlertTitle>{t('dashboard.ropVerificationBanner.title')}</AlertTitle>
          <AlertDescription>{t('dashboard.ropVerificationBanner.description')}</AlertDescription>

          <AlertActions>
            <Button
              variant="secondary"
              onClick={() => {
                navigate({
                  to: '/profile',
                });
              }}
            >
              {t('dashboard.ropVerificationBanner.verifyNow')}{' '}
              <ArrowBackFilled className={cn(i18n.dir() === 'ltr' ? 'rotate-180' : '')} />
            </Button>
          </AlertActions>
        </Alert>
      );
    }

    if (isResident && !isVerified && isPkiLoginV2) {
      return (
        <Alert variant="warning" className="mb-2">
          <AlertTitle>{t('dashboard.ropVerificationBanner.title')}</AlertTitle>
          <AlertDescription>
            {t('dashboard.ropVerificationBanner.description1')}
            <br />
            <br />
            {t('dashboard.ropVerificationBanner.description2')}
          </AlertDescription>
        </Alert>
      );
    }

    if (!isResident && !isVerified) {
      return (
        <Alert variant="warning" className="mb-2">
          <AlertTitle>{t('dashboard.mociipVerificationBanner.title')}</AlertTitle>
          <AlertDescription>{t('dashboard.mociipVerificationBanner.description')}</AlertDescription>
          <AlertActions>
            <Button asChild variant="secondary">
              <a href="https://www.business.gov.om/kyc" target="_blank">
                {t('dashboard.mociipVerificationBanner.verifyNow')}
                <OpenInNewFilled className={cn(i18n.dir() === 'rtl' ? '-rotate-90' : '')} />
              </a>
            </Button>
          </AlertActions>
        </Alert>
      );
    }

    return null;
  };

  const renderMissingNotificationSettingsBanner = () => {
    if (activeProfile?.notificationDetails.email === null || activeProfile?.notificationDetails.mobileNumber === null) {
      return (
        <Alert variant="warning" className="mb-2">
          <AlertTitle>{t('dashboard.notificationSettingsBanner.title')}</AlertTitle>
          <AlertDescription>{t('dashboard.notificationSettingsBanner.description')}</AlertDescription>
          <AlertActions>
            <Button
              variant="secondary"
              onClick={() => {
                if (activeProfile?.type === 'INVESTOR') {
                  navigate({
                    to: '/profile/investor/notification-settings',
                  });
                } else {
                  navigate({
                    to: '/profile/company/notification-settings',
                  });
                }
              }}
            >
              {t('dashboard.notificationSettingsBanner.goToNotificationSettings')}
            </Button>
          </AlertActions>
        </Alert>
      );
    }

    return null;
  };

  return (
    <>
      <SubNavContainer>
        <div className="flex gap-4 items-center">
          <Avatar size="lg" status="online">
            <AvatarImage src="/avatars/01.png" />
            <AvatarFallback>
              {getAvatarInitials(getLocalizedName(activeProfile?.nameEn, activeProfile?.nameAr, i18n.language))}
            </AvatarFallback>
          </Avatar>

          <div className="flex flex-col">
            <p className="text-xl md:text-3xl font-bold text-foreground">
              {getLocalizedName(activeProfile?.nameEn, activeProfile?.nameAr, i18n.language)}
            </p>
            <p className="text-lg md:text-xl text-muted-foreground">{activeProfile?.identifier}</p>
          </div>
        </div>
      </SubNavContainer>

      <Container>
        {/* Verification Banner */}
        {renderVerificationBanner()}

        {/* Missing notification settings e-mail / phone banner */}
        {renderMissingNotificationSettingsBanner()}

        <nav className="mb-5 grid grid-cols-2 gap-3 md:hidden" aria-label={t('dashboard.tabs.dashboard')}>
          <Button
            active={tabValue === 'workflows'}
            variant="secondary"
            aria-pressed={tabValue === 'workflows'}
            onClick={() => goToSection('/dashboard/workflows', DASHBOARD_SECTIONS.activeApplications)}
          >
            {t('dashboard.tabs.dashboard')}
          </Button>

          <PermissionGuard anyPermission={[PROFILE_PERMISSION.PMS_CONTRACT, PROFILE_PERMISSION.PMS_READ]}>
            <Button
              active={tabValue === 'contracts'}
              variant="secondary"
              aria-pressed={tabValue === 'contracts'}
              onClick={() => goToSection('/dashboard/contracts', DASHBOARD_SECTIONS.contracts)}
            >
              {t('dashboard.tabs.contracts')}
            </Button>
          </PermissionGuard>

          <PermissionGuard anyPermission={[PROFILE_PERMISSION.PMS_CONTRACT, PROFILE_PERMISSION.PMS_READ]}>
            <Button
              active={tabValue === 'rental-contracts'}
              variant="secondary"
              aria-pressed={tabValue === 'rental-contracts'}
              onClick={() => goToSection('/dashboard/rental-contracts', DASHBOARD_SECTIONS.rentalContracts)}
            >
              {t('dashboard.tabs.rentalContracts')}
            </Button>
          </PermissionGuard>

          <PermissionGuard anyPermission={[PROFILE_PERMISSION.PMS_PERMIT, PROFILE_PERMISSION.PMS_READ]}>
            <Button
              active={tabValue === 'permits'}
              variant="secondary"
              aria-pressed={tabValue === 'permits'}
              onClick={() => goToSection('/dashboard/permits', DASHBOARD_SECTIONS.permits)}
            >
              {t('dashboard.tabs.permits')}
            </Button>
          </PermissionGuard>

          <PermissionGuard anyPermission={[PROFILE_PERMISSION.PMS_CONTRACT, PROFILE_PERMISSION.PMS_READ]}>
            <Button
              active={tabValue === 'service-providers'}
              variant="secondary"
              aria-pressed={tabValue === 'service-providers'}
              onClick={() => goToSection('/dashboard/service-providers', DASHBOARD_SECTIONS.serviceProviders)}
            >
              {t('dashboard.tabs.providers')}
            </Button>
          </PermissionGuard>

          <PermissionGuard anyPermission={[PROFILE_PERMISSION.PMS_CONTRACT, PROFILE_PERMISSION.PMS_READ]}>
            <Button
              active={tabValue === 'properties'}
              variant="secondary"
              aria-pressed={tabValue === 'properties'}
              onClick={() => goToSection('/dashboard/properties', DASHBOARD_SECTIONS.properties)}
            >
              {t('dashboard.tabs.properties')}
            </Button>
          </PermissionGuard>
        </nav>

        <TabsRoot value={tabValue} dir={i18n.dir()} className="hidden md:block">
          <TabsList className="mb-6">
            <Link to="/dashboard/workflows">
              <TabsTrigger value="workflows">{t('dashboard.tabs.dashboard')}</TabsTrigger>
            </Link>

            <PermissionGuard anyPermission={[PROFILE_PERMISSION.PMS_CONTRACT, PROFILE_PERMISSION.PMS_READ]}>
              <Link to="/dashboard/contracts">
                <TabsTrigger value="contracts">{t('dashboard.tabs.contracts')}</TabsTrigger>
              </Link>
            </PermissionGuard>

            <PermissionGuard anyPermission={[PROFILE_PERMISSION.PMS_CONTRACT, PROFILE_PERMISSION.PMS_READ]}>
              <Link to="/dashboard/rental-contracts">
                <TabsTrigger value="rental-contracts">{t('dashboard.tabs.rentalContracts')}</TabsTrigger>
              </Link>
            </PermissionGuard>

            <PermissionGuard anyPermission={[PROFILE_PERMISSION.PMS_PERMIT, PROFILE_PERMISSION.PMS_READ]}>
              <Link to="/dashboard/permits">
                <TabsTrigger value="permits">{t('dashboard.tabs.permits')}</TabsTrigger>
              </Link>
            </PermissionGuard>

            <PermissionGuard anyPermission={[PROFILE_PERMISSION.PMS_CONTRACT, PROFILE_PERMISSION.PMS_READ]}>
              <Link to="/dashboard/service-providers">
                <TabsTrigger value="service-providers">{t('dashboard.tabs.providers')}</TabsTrigger>
              </Link>
            </PermissionGuard>

            <PermissionGuard anyPermission={[PROFILE_PERMISSION.PMS_CONTRACT, PROFILE_PERMISSION.PMS_READ]}>
              <Link to="/dashboard/properties">
                <TabsTrigger value="properties">{t('dashboard.tabs.properties')}</TabsTrigger>
              </Link>
            </PermissionGuard>
          </TabsList>
        </TabsRoot>

        <Outlet />
      </Container>
    </>
  );
}
